
import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import LoadingSpinner from "@/components/LoadingSpinner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { ExpenseItem, ExpenseReport, Profile, Category, displayDate } from "@/types/ExpenseReport";
import { EmailDialog } from "@/components/expense-report/EmailDialog";
import { RejectDialog } from "@/components/expense-report/RejectDialog";
import { EditExpenseDialog } from "@/components/expense-report/EditExpenseDialog";
import { ExpenseReportDetails } from "@/components/expense-report/ExpenseReportDetails";
import { ExpenseTable } from "@/components/expense-report/ExpenseTable";
import { sendNotificationEmail } from "@/utils/notificationUtils";
import type { ExtendedEmailSettings } from "@/integrations/supabase/client";
import Sidebar from "@/components/Sidebar";
import { ArrowLeft, Download, Mail, Building } from "lucide-react";
import { Button } from "@/components/ui/button";
import UserProfile from "@/components/UserProfile";
import OrganizationSelector from "@/components/OrganizationSelector";

const ExpenseReportDetail = () => {
  const { organizationId, reportId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [user, setUser] = useState<any>(null);
  const [selectedOrg, setSelectedOrg] = useState<string | null>(organizationId || null);
  const [generatedByUser, setGeneratedByUser] = useState<Profile | null>(null);
  const [approvedByUser, setApprovedByUser] = useState<Profile | null>(null);
  const [receiptUrls, setReceiptUrls] = useState<{ [key: string]: string }>({});
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);
  const [isEditExpenseDialogOpen, setIsEditExpenseDialogOpen] = useState(false);
  const [isAddExpenseDialogOpen, setIsAddExpenseDialogOpen] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [isResubmitting, setIsResubmitting] = useState(false);
  const [defaultRecipients, setDefaultRecipients] = useState<string[]>([]);
  const [defaultEmailConfig, setDefaultEmailConfig] = useState<ExtendedEmailSettings | null>(null);
  const [selectedExpense, setSelectedExpense] = useState<Partial<ExpenseItem>>({});
  const [isDeletingExpense, setIsDeletingExpense] = useState(false);
  const reportRef = useRef<HTMLDivElement>(null);
  const [shortReportId, setShortReportId] = useState<string>("");

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/login");
        return;
      }
      setUser(session.user);
    };

    checkAuth();
  }, [navigate]);

  useEffect(() => {
    if (reportId) {
      setShortReportId(reportId.split('-')[0]);
    }
  }, [reportId]);

  useEffect(() => {
    if (organizationId) {
      setSelectedOrg(organizationId);
    }
  }, [organizationId]);

  const handleOrgSelect = (orgId: string) => {
    navigate(`/app/${orgId}/expense-reports`);
  };

  const { data: report, isLoading: reportLoading, refetch: refetchReport } = useQuery({
    queryKey: ["expense-report", reportId],
    queryFn: async () => {
      if (!reportId) return null;

      const { data: reportData, error: reportError } = await supabase
        .from("expense_reports")
        .select("*")
        .eq("id", reportId)
        .single();

      if (reportError) throw reportError;

      console.log("Fetched report:", reportData);
      return reportData as ExpenseReport;
    },
    enabled: !!reportId,
  });

  const { data: generatorProfile } = useQuery({
    queryKey: ["report-generator", report?.generated_by],
    queryFn: async () => {
      if (!report?.generated_by) return null;

      const { data, error } = await supabase
        .from("profiles")
        .select("id, first_name, last_name")
        .eq("id", report.generated_by)
        .single();
      
      if (error) throw error;
      
      const profile: Profile = {
        ...data,
        email: undefined
      };
      
      setGeneratedByUser(profile);
      
      return profile;
    },
    enabled: !!report?.generated_by,
    staleTime: 0,
  });

  const { data: approverProfile } = useQuery({
    queryKey: ["report-approver", report?.approved_by],
    queryFn: async () => {
      if (!report?.approved_by) return null;

      const { data, error } = await supabase
        .from("profiles")
        .select("id, first_name, last_name")
        .eq("id", report.approved_by)
        .single();
      
      if (error) throw error;
      
      const profile: Profile = {
        ...data,
        email: undefined
      };
      
      setApprovedByUser(profile);
      
      return profile;
    },
    enabled: !!report?.approved_by,
    staleTime: 0,
  });

  useEffect(() => {
    if (generatorProfile && !generatedByUser) {
      setGeneratedByUser(generatorProfile);
    }
  }, [generatorProfile, generatedByUser]);

  useEffect(() => {
    if (approverProfile && !approvedByUser) {
      setApprovedByUser(approverProfile);
    }
  }, [approverProfile, approvedByUser]);

  useEffect(() => {
    const fetchProfiles = async () => {
      if (report?.generated_by) {
        const { data, error } = await supabase
          .from("profiles")
          .select("id, first_name, last_name")
          .eq("id", report.generated_by)
          .single();
        
        if (!error && data) {
          setGeneratedByUser(data);
        }
      }

      if (report?.approved_by) {
        const { data, error } = await supabase
          .from("profiles")
          .select("id, first_name, last_name")
          .eq("id", report.approved_by)
          .single();
        
        if (!error && data) {
          setApprovedByUser(data);
        }
      }
    };

    fetchProfiles();
  }, [report]);

  const { data: expenses, isLoading: expensesLoading, refetch: refetchExpenses } = useQuery({
    queryKey: ["expense-report-items", reportId],
    queryFn: async () => {
      if (!reportId) return [];
      
      console.log(`Fetching expense items for report ${reportId}`);
      
      const { data, error } = await supabase
        .from("expense_report_items")
        .select(`
          *,
          categories(name)
        `)
        .eq("expense_report_id", reportId);

      if (error) {
        console.error("Error fetching expense items:", error);
        throw error;
      }
      
      console.log("Fetched expense items:", data);
      
      return data.map(item => ({
        ...item,
        id: String(item.id)
      })) as ExpenseItem[];
    },
    enabled: !!reportId,
  });

  const { data: categories } = useQuery({
    queryKey: ["categories", organizationId],
    queryFn: async () => {
      if (!organizationId) return [];
      
      const { data, error } = await supabase
        .from("categories")
        .select("*")
        .eq("organization_id", organizationId);
      
      if (error) throw error;
      return data as Category[];
    },
    enabled: !!organizationId,
  });

  const { data: organization } = useQuery({
    queryKey: ["organization", organizationId],
    queryFn: async () => {
      if (!organizationId) return null;

      const { data, error } = await supabase
        .from("organizations")
        .select("*")
        .eq("id", organizationId)
        .single();

      if (error) throw error;
      
      if (data?.email_settings) {
        try {
          const emailSettings = data.email_settings;
          
          if (Array.isArray(emailSettings)) {
            const configs = emailSettings as unknown as ExtendedEmailSettings[];
            const defaultConfig = configs.find(config => config.isDefault);
            
            if (defaultConfig) {
              setDefaultEmailConfig(defaultConfig);
              
              if (defaultConfig.defaultRecipients) {
                const recipients = defaultConfig.defaultRecipients
                  .split(',')
                  .map(email => email.trim())
                  .filter(email => email !== '');
                  
                setDefaultRecipients(recipients);
              }
            }
          } else if (typeof emailSettings === 'object' && emailSettings !== null) {
            const config = emailSettings as unknown as ExtendedEmailSettings;
            setDefaultEmailConfig({
              ...config,
              name: "Default Configuration",
              isDefault: true
            });
            
            if (config.defaultRecipients) {
              const recipients = config.defaultRecipients
                .split(',')
                .map(email => email.trim())
                .filter(email => email !== '');
                
              setDefaultRecipients(recipients);
            }
          }
        } catch (error) {
          console.error("Error parsing email settings:", error);
        }
      }
      
      return data;
    },
    enabled: !!organizationId,
  });

  const { data: userRole } = useQuery({
    queryKey: ["user-role", organizationId],
    queryFn: async () => {
      if (!organizationId || !user) return null;

      console.log('Fetching user role for:', {
        organizationId,
        userId: user.id
      });

      const { data, error } = await supabase
        .from("organization_members")
        .select("role")
        .eq("organization_id", organizationId)
        .eq("user_id", user.id)
        .single();
      
      if (error) {
        console.error('Error fetching user role:', error);
        throw error;
      }
      console.log('User role fetched:', data?.role);
      return data?.role;
    },
    enabled: !!organizationId && !!user,
  });

  const isManager = userRole === 'admin' || userRole === 'manager';
  const isOwner = user?.id === report?.generated_by;
  const isDraft = report?.status === 'draft';
  const isRejected = report?.status === 'rejected';
  const canEditReport = (isDraft || isRejected) && (isOwner || isManager);
  const canResubmit = (isDraft || isRejected) && (isOwner || isManager);

  const { data: receipts, isLoading: receiptsLoading, refetch: refetchReceipts } = useQuery({
    queryKey: ["expense-receipts", expenses?.map(e => e.id)],
    queryFn: async () => {
      if (!expenses || expenses.length === 0) return [];
      
      const expenseIds = expenses.map(e => ({
        id: e.id,
        isUuid: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(e.id))
      }));
      
      const numericIds = expenseIds
        .filter(e => !e.isUuid)
        .map(e => {
          const numericId = typeof e.id === 'string' ? parseInt(e.id, 10) : e.id;
          return !isNaN(Number(numericId)) ? numericId : null;
        })
        .filter(id => id !== null) as number[];
      
      const uuidIds = expenseIds
        .filter(e => e.isUuid)
        .map(e => e.id) as string[];
      
      console.log('Fetching receipts for:', {
        numericIds,
        uuidIds
      });
      
      let receipts = [];
      if (numericIds.length > 0) {
        const { data: numericReceipts, error: numericError } = await supabase
          .from("receipts")
          .select("expense_id, file_url, file_name")
          .in("expense_id", numericIds);
        
        if (numericError) {
          console.error('Error fetching numeric receipts:', numericError);
        } else if (numericReceipts?.length > 0) {
          receipts.push(...numericReceipts);
        }
      }
      
      if (uuidIds.length > 0) {
        try {
          const { data, error } = await supabase
            .from("receipts")
            .select("expense_report_item_id")
            .limit(1);
          
          if (error && error.message.includes("column 'expense_report_item_id' does not exist")) {
            console.warn('The expense_report_item_id column does not exist in receipts table yet');
          } else {
            const { data: uuidReceipts, error: uuidError } = await supabase
              .from("receipts")
              .select("expense_report_item_id, file_url, file_name")
              .in("expense_report_item_id", uuidIds);
            
            if (uuidError && !uuidError.message.includes("column 'expense_report_item_id' does not exist")) {
              console.error('Error fetching UUID receipts:', uuidError);
            } else if (uuidReceipts && uuidReceipts.length > 0) {
              const validReceipts = uuidReceipts.filter(
                r => r && r.expense_report_item_id && r.file_url && r.file_name
              );
              
              const transformedReceipts = validReceipts.map(r => ({
                expense_id: r.expense_report_item_id,
                file_url: r.file_url,
                file_name: r.file_name
              }));
              
              receipts.push(...transformedReceipts);
            }
          }
        } catch (error) {
          console.error('Exception handling receipts by expense_report_item_id:', error);
        }
      }
      
      console.log('Fetched receipts:', receipts);
      
      return receipts;
    },
    enabled: !!expenses && expenses.length > 0,
  });

  useEffect(() => {
    const generateSignedUrls = async () => {
      const urls: { [key: string]: string } = {};
      if (!receipts || receipts.length === 0) return;

      console.log('Generating signed URLs for receipts:', receipts);

      for (const receipt of receipts) {
        const fileName = receipt.file_url.split('/').pop();
        if (!fileName) continue;

        try {
          const { data: { signedUrl }, error } = await supabase.storage
            .from('receipts')
            .createSignedUrl(fileName, 3600);

          if (error) {
            console.error(`Error creating signed URL for file ${fileName}:`, error);
            continue;
          }

          if (signedUrl) {
            urls[receipt.expense_id.toString()] = signedUrl;
            console.log(`Created signed URL for expense ID ${receipt.expense_id}`);
          }
        } catch (err) {
          console.error(`Exception creating signed URL for file ${fileName}:`, err);
        }
      }

      console.log('Final receipt URLs:', urls);
      setReceiptUrls(urls);
    };

    generateSignedUrls();
  }, [receipts]);

  const handleReceiptUploadComplete = (fileUrl: string, fileName: string) => {
    console.log('Receipt upload completed:', fileName, fileUrl);
    refetchReceipts();
    refetchExpenses();
    toast({
      title: "Success",
      description: "Receipt uploaded successfully",
    });
  };

  const handleApprove = async () => {
    if (!reportId || !user) return;

    try {
      const { error } = await supabase
        .from('expense_reports')
        .update({
          status: 'approved',
          approved_by: user.id,
          approval_date: new Date().toISOString(),
        })
        .eq('id', reportId);

      if (error) {
        console.error("Error approving report:", error);
        toast({
          title: "Error",
          description: "Failed to approve report",
          variant: "destructive",
        });
        return;
      }

      refetchReport();
      queryClient.invalidateQueries({ queryKey: ['expense-reports', organizationId] });
      
      toast({
        title: "Success",
        description: "Expense report approved successfully",
      });
      
      if (organization && organization.email_settings) {
        try {
          const recipient = generatorProfile?.email || generatorProfile?.id;
          
          if (recipient) {
            const subject = `Expense Report ${shortReportId} Approved`;
            const message = `Your expense report ${shortReportId} has been approved.`;
            
            await sendNotificationEmail({
              recipient: recipient,
              subject: subject,
              message: message,
              reportStatus: 'approved'
            });
          }
        } catch (error) {
          console.error("Error sending notification email:", error);
        }
      }
    } catch (error) {
      console.error("Error in approval process:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive",
      });
    }
  };

  const handleReject = async (comments: string) => {
    if (!reportId || !user) return;

    try {
      const { error } = await supabase
        .from('expense_reports')
        .update({
          status: 'rejected',
          approved_by: user.id,
          approval_date: new Date().toISOString(),
          rejection_comments: comments
        })
        .eq('id', reportId);

      if (error) {
        console.error("Error rejecting report:", error);
        toast({
          title: "Error",
          description: "Failed to reject report",
          variant: "destructive",
        });
        return;
      }

      refetchReport();
      queryClient.invalidateQueries({ queryKey: ['expense-reports', organizationId] });
      
      toast({
        title: "Success",
        description: "Expense report rejected successfully",
      });
      
      if (organization && organization.email_settings) {
        try {
          const recipient = generatorProfile?.email || generatorProfile?.id;
          
          if (recipient) {
            const subject = `Expense Report ${shortReportId} Rejected`;
            const message = `Your expense report ${shortReportId} has been rejected.`;
            
            await sendNotificationEmail({
              recipient: recipient,
              subject: subject,
              message: message,
              reportStatus: 'rejected',
              rejectionComments: comments
            });
          }
        } catch (error) {
          console.error("Error sending notification email:", error);
        }
      }
    } catch (error) {
      console.error("Error in rejection process:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive",
      });
    }
  };

  const handleSubmit = async () => {
    if (!reportId || !user) return;
    
    setIsResubmitting(true);
    
    try {
      const { error } = await supabase
        .from('expense_reports')
        .update({
          status: 'pending',
          approved_by: null,
          approval_date: null,
          rejection_comments: null
        })
        .eq('id', reportId);

      if (error) {
        console.error("Error resubmitting report:", error);
        toast({
          title: "Error",
          description: "Failed to resubmit report",
          variant: "destructive",
        });
        setIsResubmitting(false);
        return;
      }

      refetchReport();
      queryClient.invalidateQueries({ queryKey: ['expense-reports', organizationId] });
      
      toast({
        title: "Success",
        description: "Expense report submitted for approval",
      });
      
      setIsResubmitting(false);
    } catch (error) {
      console.error("Error in resubmission process:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive",
      });
      setIsResubmitting(false);
    }
  };

  const handleSendEmail = async (email: string) => {
    if (!reportId || !report) return;
    
    setIsSendingEmail(true);
    
    try {
      const doc = new jsPDF();
      
      // Add report header
      doc.setFontSize(20);
      doc.text(`Expense Report #${shortReportId}`, 20, 20);
      
      doc.setFontSize(12);
      doc.text(`Status: ${report.status}`, 20, 30);
      doc.text(`From: ${displayDate(report.date_from)}`, 20, 40);
      doc.text(`To: ${displayDate(report.date_to)}`, 20, 50);
      doc.text(`Created by: ${generatedByUser?.first_name || ''} ${generatedByUser?.last_name || ''}`, 20, 60);
      doc.text(`Total amount: ${report.total_amount.toFixed(2)} ${report.currency}`, 20, 70);
      
      // Add expense table
      if (expenses && expenses.length > 0) {
        const tableColumn = ["Date", "Description", "Category", "Amount"];
        const tableRows = expenses.map(expense => [
          displayDate(expense.date),
          expense.description,
          expense.categories?.name || '',
          `${expense.amount.toFixed(2)} ${expense.currency}`
        ]);
        
        autoTable(doc, {
          head: [tableColumn],
          body: tableRows,
          startY: 90,
          theme: 'striped',
          headStyles: { fillColor: [66, 135, 245] },
        });
      }
      
      // Convert to base64
      const pdfDataUrl = doc.output('datauristring');
      
      // Send the email
      const formData = new FormData();
      formData.append('recipient', email);
      formData.append('reportId', reportId);
      formData.append('subject', `Expense Report #${shortReportId}`);
      formData.append('pdfAttachment', pdfDataUrl);
      
      const response = await fetch('/api/send-expense-report', {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        throw new Error('Failed to send email');
      }
      
      // Update the report record with the email details
      await supabase
        .from('expense_reports')
        .update({
          email_sent_at: new Date().toISOString(),
          email_sent_to: email
        })
        .eq('id', reportId);
      
      toast({
        title: "Success",
        description: `Email sent to ${email}`,
        variant: "default"
      });
      
      refetchReport();
    } catch (error) {
      console.error('Error sending email:', error);
      toast({
        title: "Error",
        description: "Failed to send email",
        variant: "destructive"
      });
    } finally {
      setIsSendingEmail(false);
      setIsEmailDialogOpen(false);
    }
  };

  const handleOpenAddExpenseDialog = () => {
    setSelectedExpense({});
    setIsAddExpenseDialogOpen(true);
  };

  const handleOpenEditExpenseDialog = (expense: ExpenseItem) => {
    setSelectedExpense(expense);
    setIsEditExpenseDialogOpen(true);
  };

  const handleSaveExpense = async (expenseData: Partial<ExpenseItem>) => {
    if (!reportId || !organizationId || !user) return;
    
    try {
      if (expenseData.id) {
        // Update existing expense
        const { error } = await supabase
          .from('expense_report_items')
          .update({
            description: expenseData.description,
            amount: expenseData.amount,
            date: expenseData.date,
            category_id: expenseData.category_id,
            currency: expenseData.currency
          })
          .eq('id', expenseData.id);
        
        if (error) {
          throw error;
        }
        
        toast({
          title: "Success",
          description: "Expense updated successfully",
        });
      } else {
        // Create new expense
        const { error } = await supabase
          .from('expense_report_items')
          .insert({
            expense_report_id: reportId,
            organization_id: organizationId,
            created_by: user.id,
            description: expenseData.description,
            amount: expenseData.amount,
            date: expenseData.date,
            category_id: expenseData.category_id,
            currency: expenseData.currency,
          });
        
        if (error) {
          throw error;
        }
        
        toast({
          title: "Success",
          description: "Expense added successfully",
        });
      }
      
      // Refetch expenses and update total
      await refetchExpenses();
      
      // Calculate new total
      const { data: updatedExpenses } = await supabase
        .from('expense_report_items')
        .select('amount,currency')
        .eq('expense_report_id', reportId);
      
      if (updatedExpenses) {
        const newTotal = updatedExpenses.reduce((sum, exp) => sum + Number(exp.amount), 0);
        
        await supabase
          .from('expense_reports')
          .update({
            total_amount: newTotal
          })
          .eq('id', reportId);
        
        refetchReport();
      }
    } catch (error) {
      console.error('Error saving expense:', error);
      toast({
        title: "Error",
        description: "Failed to save expense",
        variant: "destructive",
      });
    }
  };

  const handleDeleteExpense = async (expenseId: string) => {
    if (!reportId || !expenseId) return;
    
    setIsDeletingExpense(true);
    
    try {
      const { error } = await supabase
        .from('expense_report_items')
        .delete()
        .eq('id', expenseId)
        .eq('expense_report_id', reportId);
      
      if (error) {
        throw error;
      }
      
      // Refetch expenses and update total
      await refetchExpenses();
      
      // Calculate new total
      const { data: updatedExpenses } = await supabase
        .from('expense_report_items')
        .select('amount,currency')
        .eq('expense_report_id', reportId);
      
      if (updatedExpenses) {
        const newTotal = updatedExpenses.reduce((sum, exp) => sum + Number(exp.amount), 0);
        
        await supabase
          .from('expense_reports')
          .update({
            total_amount: newTotal
          })
          .eq('id', reportId);
        
        refetchReport();
      }
      
      toast({
        title: "Success",
        description: "Expense deleted successfully",
      });
    } catch (error) {
      console.error('Error deleting expense:', error);
      toast({
        title: "Error",
        description: "Failed to delete expense",
        variant: "destructive",
      });
    } finally {
      setIsDeletingExpense(false);
    }
  };

  const handleGeneratePdf = () => {
    if (!report) return;
    
    const doc = new jsPDF();
    
    // Add report header
    doc.setFontSize(20);
    doc.text(`Expense Report #${shortReportId}`, 20, 20);
    
    doc.setFontSize(12);
    doc.text(`Status: ${report.status}`, 20, 30);
    doc.text(`From: ${displayDate(report.date_from)}`, 20, 40);
    doc.text(`To: ${displayDate(report.date_to)}`, 20, 50);
    doc.text(`Created by: ${generatedByUser?.first_name || ''} ${generatedByUser?.last_name || ''}`, 20, 60);
    if (report.approved_by) {
      doc.text(`Approved by: ${approvedByUser?.first_name || ''} ${approvedByUser?.last_name || ''}`, 20, 70);
      doc.text(`Approval date: ${report.approval_date ? displayDate(report.approval_date) : 'N/A'}`, 20, 80);
      doc.text(`Total amount: ${report.total_amount.toFixed(2)} ${report.currency}`, 20, 90);
    } else {
      doc.text(`Total amount: ${report.total_amount.toFixed(2)} ${report.currency}`, 20, 70);
    }
    
    // Add expense table
    if (expenses && expenses.length > 0) {
      const tableColumn = ["Date", "Description", "Category", "Amount"];
      const tableRows = expenses.map(expense => [
        displayDate(expense.date),
        expense.description,
        expense.categories?.name || '',
        `${expense.amount.toFixed(2)} ${expense.currency}`
      ]);
      
      const startY = report.approved_by ? 100 : 80;
      
      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: startY,
        theme: 'striped',
        headStyles: { fillColor: [66, 135, 245] },
      });
    }
    
    // Save the PDF
    doc.save(`expense-report-${shortReportId}.pdf`);
  };

  if (reportLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!report) {
    return (
      <div className="flex h-full items-center justify-center">
        <Alert variant="destructive" className="max-w-lg">
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            Could not load the expense report. It may have been deleted or you don't have permission to view it.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="flex h-screen">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header Section */}
        <header className="border-b bg-white px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-md bg-gray-100 flex items-center justify-center text-primary">
                <Mail className="h-5 w-5" />
              </div>
              <h1 className="text-xl font-bold">Expense</h1>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-md bg-gray-100 flex items-center justify-center text-primary">
                <Building className="h-5 w-5" />
              </div>
              <OrganizationSelector 
                selectedOrg={selectedOrg} 
                onSelect={handleOrgSelect} 
                showBackButton={false}
              />
            </div>
          </div>
          {user && <UserProfile user={user} />}
        </header>

        <div className="flex-1 overflow-auto">
          <div className="container mx-auto py-6 space-y-8 px-4 sm:px-6">
            <div className="flex flex-col gap-2">
              <div className="flex items-center">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="mr-2"
                  onClick={() => navigate(`/app/${organizationId}/expense-reports`)}
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Back
                </Button>
                <h1 className="text-3xl font-bold tracking-tight">Expense Report Details</h1>
              </div>
              <p className="text-muted-foreground">
                View and manage all details of this expense report
              </p>
            </div>

            <div className="flex justify-between items-center flex-wrap gap-2">
              <div></div>
              <div className="space-x-2">
                {report.status === 'approved' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsEmailDialogOpen(true)}
                    disabled={isSendingEmail}
                  >
                    <Mail className="mr-2 h-4 w-4" />
                    Email Report
                  </Button>
                )}
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleGeneratePdf}
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download PDF
                </Button>
              </div>
            </div>
            
            <div ref={reportRef}>
              <ExpenseReportDetails 
                report={report}
                generatedByUser={generatedByUser}
                approvedByUser={approvedByUser}
                isManager={isManager}
                isOwner={isOwner}
                isDraft={isDraft}
                isRejected={isRejected}
                canResubmit={canResubmit}
                isResubmitting={isResubmitting}
                shortReportId={shortReportId}
                onApprove={handleApprove}
                onReject={() => setIsRejectDialogOpen(true)}
                onSubmit={handleSubmit}
              />
              
              <ExpenseTable 
                expenses={expenses}
                expensesLoading={expensesLoading}
                canEditReport={canEditReport}
                receiptUrls={receiptUrls}
                handleReceiptUploadComplete={handleReceiptUploadComplete}
                handleOpenEditExpenseDialog={handleOpenEditExpenseDialog}
                handleOpenAddExpenseDialog={handleOpenAddExpenseDialog}
                handleDeleteExpense={handleDeleteExpense}
                isDeletingExpense={isDeletingExpense}
                organizationId={organizationId || ""}
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Dialogs */}
      <EmailDialog 
        open={isEmailDialogOpen}
        onClose={() => setIsEmailDialogOpen(false)}
        onSend={handleSendEmail}
        defaultEmail=""
        defaultRecipients={defaultRecipients}
      />
      
      <RejectDialog 
        open={isRejectDialogOpen}
        onClose={() => setIsRejectDialogOpen(false)}
        onReject={handleReject}
      />
      
      <EditExpenseDialog 
        open={isEditExpenseDialogOpen}
        onClose={() => setIsEditExpenseDialogOpen(false)}
        expense={selectedExpense}
        onSave={handleSaveExpense}
        categories={categories || []}
        organizationId={organizationId || ""}
        isNew={false}
        reportCurrency={report?.currency || "USD"}
      />
      
      <EditExpenseDialog 
        open={isAddExpenseDialogOpen}
        onClose={() => setIsAddExpenseDialogOpen(false)}
        expense={selectedExpense}
        onSave={handleSaveExpense}
        categories={categories || []}
        organizationId={organizationId || ""}
        isNew={true}
        reportCurrency={report?.currency || "USD"}
      />
    </div>
  );
};

export default ExpenseReportDetail;
